import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Plus, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { paymentService } from '../../services/payment.service';
import { PaymentStatus } from '../../types';
import { RecordPaymentModal } from './RecordPaymentModal';

export const PaymentsPage: React.FC = () => {
  const [isRecordModalOpen, setIsRecordModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: payments = [], isLoading } = useQuery({
    queryKey: ['payments'],
    queryFn: paymentService.getAll,
  });

  const verifyMutation = useMutation({
    mutationFn: (id: number) => paymentService.verify(id),
    onSuccess: () => {
      toast.success('Payment verified successfully');
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to verify payment');
    },
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      paymentService.cancel(id, reason),
    onSuccess: () => {
      toast.success('Payment cancelled successfully');
      queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to cancel payment');
    },
  });

  const handleCancelClick = (id: number) => {
    const reason = prompt('Enter cancellation reason:');
    if (reason) {
      cancelMutation.mutate({ id, reason });
    }
  };

  const getStatusBadgeVariant = (status: PaymentStatus) => {
    switch (status) {
      case PaymentStatus.RECORDED:
        return 'warning';
      case PaymentStatus.VERIFIED:
        return 'success';
      case PaymentStatus.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="space-y-6">
      <Card
        title="Payments"
        action={
          <Button size="sm" onClick={() => setIsRecordModalOpen(true)}>
            <Plus size={16} className="mr-2" />
            Record Payment
          </Button>
        }
      >
        {isLoading && <div className="text-sm text-gray-600">Loading...</div>}

        {!isLoading && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Payment Ref</TableHead>
                  <TableHead>Payment Date</TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Payer</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Transaction Ref</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" colSpan={9}>
                      No payments found.
                    </td>
                  </TableRow>
                ) : (
                  payments.map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell className="font-medium">{payment.paymentReference}</TableCell>
                      <TableCell>{format(parseISO(payment.paymentDate), 'MMM dd, yyyy')}</TableCell>
                      <TableCell>{payment.invoiceNumber}</TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{payment.payerName}</div>
                          {payment.payerContact && (
                            <div className="text-xs text-gray-500">{payment.payerContact}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{formatCurrency(payment.amount)}</TableCell>
                      <TableCell>
                        <Badge variant="info">{payment.paymentMethod.replace('_', ' ')}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {payment.transactionReference || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusBadgeVariant(payment.status)}>
                          {payment.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {payment.status === PaymentStatus.RECORDED && (
                            <>
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => verifyMutation.mutate(payment.id)}
                                loading={verifyMutation.isPending}
                              >
                                <CheckCircle size={14} className="mr-1" />
                                Verify
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleCancelClick(payment.id)}
                                loading={cancelMutation.isPending}
                              >
                                <XCircle size={14} className="mr-1" />
                                Cancel
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <RecordPaymentModal
        isOpen={isRecordModalOpen}
        onClose={() => setIsRecordModalOpen(false)}
      />
    </div>
  );
};
